export const cleanPhone = (phone: string | null | undefined): string => {
    return (phone || '').replace(/\D/g, '');
};

/** Placeholders IG (seed webhook / mapper) — única fonte SPA + edge. */
const INSTAGRAM_PLACEHOLDER_NAME =
    /^(instagram(\s+direct)?|contato\s+instagram|conta\s+instagram)$/i;

/** True quando o rótulo ainda é genérico (sem nome/@ real). */
export function isGenericInstagramLabel(name?: string | null): boolean {
    const trimmed = (name || '').trim();
    if (!trimmed) return true;
    return INSTAGRAM_PLACEHOLDER_NAME.test(trimmed);
}

/** `@username` estável a partir do handle Graph (sem duplicar `@`). */
export function formatInstagramHandle(username?: string | null): string | undefined {
    const handle = username?.trim().replace(/^@+/, '');
    return handle ? `@${handle}` : undefined;
}

/** Rejeita nomes inválidos vindos do WhatsApp/Uazapi (ex.: ".", "-"). */
export function isUsableContactName(name: string | undefined | null, phone?: string): boolean {
    if (!name?.trim()) return false;
    const trimmed = name.trim();
    if (trimmed.length < 2) return false;
    if (/^[\s.\-_?*#@!~]+$/.test(trimmed)) return false;
    if (INSTAGRAM_PLACEHOLDER_NAME.test(trimmed)) return false;
    if (phone) {
        const digitsOnly = trimmed.replace(/\D/g, '');
        const phoneDigits = cleanPhone(phone);
        if (digitsOnly && phoneDigits && digitsOnly === phoneDigits) return false;
        if (/^\+?[\d\s().-]+$/.test(trimmed)) return false;
    }
    return true;
}

/**
 * Normaliza um identificador para o formato JID do WhatsApp.
 * @param id - Número ou JID parcial
 * @returns JID completo no formato "número@s.whatsapp.net"
 */
export const normalizeJid = (id: string): string => {
    if (id.includes('@')) return id;
    const clean = cleanPhone(id);
    return `${clean}@s.whatsapp.net`;
};

/**
 * Extrai apenas o número de um JID do WhatsApp.
 * @param jid - JID no formato "número@s.whatsapp.net"
 * @returns Apenas o número sem sufixo
 */
export const extractNumber = (jid: string): string => {
    return jid.split('@')[0].replace(/\D/g, '');
};

/** Número para POST /send/* da Uazapi — preserva o JID da conversa (só garante DDI 55). */
export const toUazapiSendNumber = (phoneOrJid: string): string => {
    let num = extractNumber(phoneOrJid);
    if (!num) return num;
    if (!num.startsWith('55') && num.length >= 10 && num.length <= 11) {
        num = addBrazilPrefix(num);
    }
    return num;
};

/**
 * Adiciona prefixo BR se necessário (55).
 * @param phone - Número limpo
 * @returns Número com código do país
 */
export const addBrazilPrefix = (phone: string): string => {
    const clean = cleanPhone(phone);
    if (clean.length <= 11) {
        return `55${clean}`;
    }
    return clean;
};

/** Celular BR no formato WhatsApp: 55 + DDD + 8 dígitos (sem 9º dígito). */
export const stripBrazilMobileNinthDigit = (phone: string): string => {
    if (phone.startsWith('55') && phone.length === 13 && phone.charAt(4) === '9') {
        return phone.slice(0, 4) + phone.slice(5);
    }
    return phone;
};

const brMobileCanonical = (digits: string): string =>
    stripBrazilMobileNinthDigit(addBrazilPrefix(digits));

/** Chave canônica para desduplicação de contatos (DDI 55, sem 9º dígito em celular). */
export const contactPhoneKey = (raw: string | null | undefined): string | null => {
    const digits = cleanPhone(raw);
    if (!digits) return null;
    return brMobileCanonical(digits);
};

/** Variantes com/sem 9º dígito para busca de duplicatas legadas. */
export const contactPhoneKeyVariants = (raw: string | null | undefined): string[] => {
    const key = contactPhoneKey(raw);
    if (!key) return [];
    const variants = new Set<string>([key]);
    if (key.startsWith('55') && key.length === 12) {
        variants.add(`${key.slice(0, 4)}9${key.slice(4)}`);
    }
    return [...variants];
};

/**
 * Normaliza telefone/JID para formato canônico do chat WhatsApp (BR com DDI 55).
 */
export const normalizePhoneForChat = (phoneOrJid: string): string => {
    const number = brMobileCanonical(extractNumber(phoneOrJid));
    return normalizeJid(number);
};

/**
 * Verifica se dois JIDs representam o mesmo contato (ignora DDI, 9º dígito e formatação).
 * Instagram (`ig:…`) só casa por igualdade exata — nunca por dígitos de telefone.
 */
export const jidsMatch = (jid1: string, jid2: string): boolean => {
    if (!jid1 || !jid2) return false;
    if (jid1 === jid2) return true;
    if (jid1.startsWith('ig:') || jid2.startsWith('ig:')) return false;
    return brMobileCanonical(extractNumber(jid1)) === brMobileCanonical(extractNumber(jid2));
};

const addBrazilNinthDigitVariants = (variants: Set<string>, num: string): void => {
    if (!num.startsWith('55')) return;

    // Tem 9º dígito (13 chars: 55 + DD + 9 + 8) -> Remove
    if (num.length === 13) {
        const without9 = num.substring(0, 4) + num.substring(5);
        variants.add(without9);
        variants.add(`${without9}@s.whatsapp.net`);
    }

    // NÃO tem 9º dígito (12 chars: 55 + DD + 8) -> Adiciona
    if (num.length === 12) {
        const with9 = num.substring(0, 4) + '9' + num.substring(4);
        variants.add(with9);
        variants.add(`${with9}@s.whatsapp.net`);
    }
};

/**
 * Gera variantes de JID para busca no banco (com/sem 9, com/sem @, com/sem DDI 55).
 * Útil para lidar com inconsistências de formato.
 * @param id - ID da conversa
 * @returns Array de possíveis JIDs para busca
 */
export const generateJidVariants = (id: string): string[] => {
    if (id.startsWith('ig:')) return [id];

    const variants = new Set<string>([
        id,
        id.includes('@') ? id : `${id}@s.whatsapp.net`,
        id.split('@')[0],
    ]);

    const justNumber = id.split('@')[0].replace(/\D/g, '');
    variants.add(justNumber);
    variants.add(`${justNumber}@s.whatsapp.net`);

    const numbersToProcess = new Set<string>([justNumber]);

    // Números BR sem DDI (10–11 dígitos) → inclui variantes com 55
    if (!justNumber.startsWith('55') && justNumber.length >= 10 && justNumber.length <= 11) {
        numbersToProcess.add(`55${justNumber}`);
    }

    // Número com DDI → inclui variante sem 55
    if (justNumber.startsWith('55') && justNumber.length > 11) {
        numbersToProcess.add(justNumber.substring(2));
    }

    for (const num of numbersToProcess) {
        variants.add(num);
        variants.add(`${num}@s.whatsapp.net`);
        addBrazilNinthDigitVariants(variants, num);
    }

    return [...variants];
};

/**
 * Compara dois números de telefone ignorando formatação.
 * @param phone1 - Primeiro telefone
 * @param phone2 - Segundo telefone
 * @returns true se representam o mesmo número
 */
export const comparePhones = (phone1: string, phone2: string): boolean => {
    const clean1 = cleanPhone(phone1);
    const clean2 = cleanPhone(phone2);

    // Retorna false se um dos números for vazio, evitando falsos positivos (ex: "123".includes(""))
    if (!clean1 || !clean2) return false;

    // Comparação flexível: um contém o outro (para lidar com prefixos)
    return clean1.includes(clean2) || clean2.includes(clean1);
};
export const isValidJid = (jid: string | null | undefined): boolean => {
    if (!jid || typeof jid !== 'string') return false;
    if (jid.includes('undefined') || jid.includes('null')) return false;
    if (jid.startsWith('ig:') && jid.length > 3) return true;

    // Se for UUID, é um ID de conversa válido do banco de dados (Adlitics V2)
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(jid);
    if (isUUID) return true;

    // Se for grupo, aceita formato de grupo
    if (jid.includes('@g.us')) return true;

    // Se for usuário (@s.whatsapp.net ou sem sufixo mas tratado depois)
    const number = jid.split('@')[0].replace(/\D/g, '');

    // NENHUM país tem número menor que 7 dígitos (considerando DDI)
    // DDI (1-3) + DDD (2-3) + Num (4-10) => Minimo seguro ~7-8
    return number.length >= 7;
};
